import { useId, useState } from 'react';
import type { FC } from 'react';
import {
  Button,
  Flex,
  Grid,
  Heading,
  Section,
  Text,
} from '@highereducation/hen-ui';
import type { SubmittedProgram } from '../ResultsPage/types';
import SubmissionsCard from './SubmissionsCard';

type SubmissionsProps = {
  submissions: SubmittedProgram[];
  firstName?: string;
  /** Author-configured heading. Falls back to the default copy when unset. */
  title?: string;
  /** Author-configured subheading. Falls back to the default copy when unset. */
  subtitle?: string;
  onViewNextSteps?: (submission: SubmittedProgram) => void;
};

const Submissions: FC<SubmissionsProps> = ({
  submissions,
  firstName,
  title,
  subtitle,
  onViewNextSteps,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const cardsId = useId();

  if (!submissions?.length) return null;

  return (
    <Section
      as='section'
      variant='default'
      padding='section'
      className='my-lg bg-primary-100'
    >
      <Flex
        direction='column'
        gap='2xl'
        width='full'
        className='mx-auto max-w-[1536px]'
      >
        <Flex direction='row' align='start' gap='md' asChild>
          <header>
            <Flex direction='column' gap='none' className='flex-1'>
              <Heading variant='h2' className='mb-xxs'>
                {title ??
                  `Good news${
                    firstName ? `, ${firstName}` : ''
                  }! You're ready for the next step.`}
              </Heading>
              {subtitle ? (
                <Text variant='b3'>{subtitle}</Text>
              ) : (
                <Text variant='b3'>
                  Select{' '}
                  <Text tag='b' variant='b3' weight='bold'>
                    Next Steps
                  </Text>{' '}
                  to learn about what you can expect from your call with a rep
                  and how to apply.
                </Text>
              )}
            </Flex>
            <Button
              variant='iconOnly'
              iconAfter='chevron-up'
              type='button'
              aria-expanded={isOpen}
              aria-controls={cardsId}
              aria-label={
                isOpen ? 'Collapse submissions list' : 'Expand submissions list'
              }
              onClick={() => setIsOpen((prev) => !prev)}
              className={
                isOpen
                  ? 'rotate-180 transition-transform'
                  : 'transition-transform'
              }
            />
          </header>
        </Flex>
        {isOpen && (
          <Grid columns={1} gap='sm' id={cardsId}>
            {submissions.map((submission, index) => (
              <SubmissionsCard
                key={submission.program.id}
                data={submission}
                position={index + 1}
                onViewNextSteps={onViewNextSteps}
              />
            ))}
          </Grid>
        )}
      </Flex>
    </Section>
  );
};

export default Submissions;
